import { useTranslation } from 'locales/index';
import { get } from 'lodash-es';
import { flat, isDefined, isNonNullish, isNullish, isNumber, map, pipe, prop } from 'remeda';
import type { Automation } from '../../../api';
import { useAutomationWizardFormContext } from './automation-wizard-form';
import {
  DISABLED_INPUT_FIELD_PROPS,
  FIELD_TYPE_TO_ICON,
  HIDDEN_INPUT_FIELD_PROPS,
  useComputeFields,
} from './condition-fields-constants';
import type { AutomationFieldType } from './field-types';
import { getEmptyAction, getEmptyCondition } from './initial-state';
import { getOperatorDefinition, OPERATOR_DEFINITIONS, useOperators } from './operators';

export function useAutomationConditionsController() {
  const { t } = useTranslation();
  const form = useAutomationWizardFormContext();
  const automation = form.getValues();

  const operators = useOperators();

  const fields = useComputeFields(automation.targetModel);
  const flattenedFields = pipe(fields, map(prop('items')), flat());

  //* MARK: - getCondition
  const getCondition = (index: number) => get(automation.conditions, index, getEmptyCondition());

  //* MARK: addCondition
  const addCondition = () => form.insertListItem('conditions', getEmptyCondition());

  //* MARK: removeCondition
  const removeCondition = (conditionIndex: number) =>
    form.removeListItem('conditions', conditionIndex);

  //* MARK: selectConditionField
  const selectConditionField = (conditionIndex: number, fieldName: string) => {
    const field = flattenedFields.find(f => f.value === fieldName);
    if (isNullish(field)) return;

    const firstMatchingOperator = operators[field.type][0].value;
    form.setFieldValue(`conditions.${conditionIndex}.fieldName`, field.value);
    form.setFieldValue(`conditions.${conditionIndex}.operator`, firstMatchingOperator);
    const { defaultOperandValue } = getOperatorDefinition(firstMatchingOperator);
    form.setFieldValue(`conditions.${conditionIndex}.operand`, defaultOperandValue);
  };

  //* MARK: getConditionFieldType
  type Condition = Automation['conditions'][number];
  function getConditionFieldType(conditionIndex: number): AutomationFieldType | null;
  function getConditionFieldType(condition: Condition): AutomationFieldType | null;
  function getConditionFieldType(conditionOrIndex: Condition | number): AutomationFieldType | null {
    let condition: Condition;
    if (isNumber(conditionOrIndex)) condition = getCondition(conditionOrIndex);
    else condition = conditionOrIndex;

    const field = flattenedFields.find(f => f.value === condition.fieldName);
    return field ? field.type : null;
  }

  //* MARK: isConditionValid
  const isConditionValid = (condition: Automation['conditions'][number]): boolean => {
    if (isNullish(condition.fieldName)) return false;
    if (isNullish(condition.operator)) return false;

    const { isOperandValid } = getOperatorDefinition(condition.operator);

    return isOperandValid(condition.operand);
  };

  //* MARK: isConditionEmpty
  const isConditionEmpty = (condition: Automation['conditions'][number]) =>
    isNullish(condition.fieldName);

  //* MARK: isConditionValidOrEmpty
  const isConditionValidOrEmpty = (condition: Automation['conditions'][number]) =>
    isConditionValid(condition) || isConditionEmpty(condition);

  //* MARK: getOperatorFieldProps
  const getOperatorFieldProps = (conditionIndex: number) => {
    const condition = getCondition(conditionIndex);
    if (isNullish(condition)) return DISABLED_INPUT_FIELD_PROPS;

    const fieldType = flattenedFields.find(f => f.value === condition.fieldName)?.type;
    if (isNullish(fieldType)) return DISABLED_INPUT_FIELD_PROPS;

    const leftSection = isNullish(fieldType)
      ? null
      : OPERATOR_DEFINITIONS[fieldType].find(op => op.value === condition.operator)?.icon || null;

    return {
      data: operators[fieldType],
      leftSection,
    };
  };

  //* MARK: getFieldFieldProps
  const getFieldFieldProps = (conditionIndex: number) => {
    const condition = getCondition(conditionIndex);
    const fieldType = flattenedFields.find(f => f.value === condition.fieldName)?.type;

    return {
      data: fields,
      leftSection: isNullish(fieldType) ? null : FIELD_TYPE_TO_ICON[fieldType],
    };
  };

  //* MARK: isOperandFieldVisible
  const isOperandFieldVisible = (conditionIndex: number) => {
    const condition = getCondition(conditionIndex);
    if (isNullish(condition.operator)) return true;
    return getOperatorDefinition(condition.operator).hasOperand;
  };

  //* MARK: getOperandFieldProps
  const getOperandFieldProps = (conditionIndex: number) => {
    const condition = getCondition(conditionIndex);
    if (isNullish(condition.operator)) return DISABLED_INPUT_FIELD_PROPS;
    if (!getOperatorDefinition(condition.operator).hasOperand) return HIDDEN_INPUT_FIELD_PROPS;

    const field = flattenedFields.find(f => f.value === condition.fieldName);
    if (field?.type === 'select' && isDefined(field.options)) {
      const fieldKey = field.value.toUpperCase().replaceAll('_', '');
      return {
        data: field.options.map(option => ({
          value: option,
          label: t(`AUTOMATION.OPTIONS.INVOICE.${fieldKey}.${option.toUpperCase()}`),
        })),
      };
    }

    return {};
  };

  //* MARK: reset
  // Reset conditions and actions when the target model changes
  // We prevent this from happening when we're editing
  form.watch('targetModel', () => {
    const currentValues = form.getValues();
    const isCurrentlyEditMode = isNonNullish(currentValues.id);

    if (isCurrentlyEditMode) return;

    form.setValues({
      ...currentValues,
      conditions: [getEmptyCondition()],
      actions: [getEmptyAction()],
    });
  });

  return {
    conditionsCount: automation.conditions.length,
    addCondition,
    removeCondition,
    selectConditionField,
    getConditionFieldType,
    isConditionValid,
    isConditionEmpty,
    isConditionValidOrEmpty,
    getFieldFieldProps,
    getOperatorFieldProps,
    isOperandFieldVisible,
    getOperandFieldProps,
  };
}
